<template>
  <el-dialog
      v-model="dialogVisible"
      title=""
      width="420px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      class="bind-phone-dialog"
  >

    <template #header>
      <div class="dialog-custom-header">
        <span class="dialog-title">绑定手机</span>
      </div>
    </template>

    <el-form :model="bindForm" ref="bindFormRef">
      <el-form-item prop="phone">
        <el-input
            v-model="bindForm.phone"
            placeholder="请输入手机号"
            size="large"
            clearable
            @input="clearPhoneError"
        >
          <template #prefix>
            <el-icon><Iphone /></el-icon>
          </template>
        </el-input>
        <div v-if="phoneError" class="error-message">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ phoneError }}</span>
        </div>
      </el-form-item>

      <el-form-item prop="captcha">
        <el-input
            v-model="bindForm.captcha"
            placeholder="请输入验证码"
            size="large"
            @input="clearCaptchaError"
        >
          <template #prefix>
            <el-icon><Message /></el-icon>
          </template>
          <template #suffix>
            <el-button
                link
                class="captcha-btn"
                :disabled="captchaCooldown > 0"
                @click="sendCaptcha"
            >
              {{ captchaCooldown > 0 ? `${captchaCooldown}秒后重试` : '获取验证码' }}
            </el-button>
          </template>
        </el-input>
        <div v-if="captchaError" class="error-message">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ captchaError }}</span>
        </div>
        <div v-if="captchaSuccess" class="success-message">
          <el-icon><SuccessFilled /></el-icon>
          <span>{{ captchaSuccess }}</span>
        </div>
      </el-form-item>
    </el-form>

    <!-- 微信绑定提示 -->
    <div class="wechat-bind-tip">
      <p>绑定成功后，系统将自动为您创建账号</p>
    </div>

    <template #footer>
      <el-button
          type="primary"
          @click="handleBind"
          :loading="loading"
          style="width: 100%"
      >
        绑定手机
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import {ref, reactive, watch} from 'vue'
import {ElMessage} from 'element-plus'
import {Iphone, Message, WarningFilled, SuccessFilled} from '@element-plus/icons-vue'
import request from '@/utils/request'
import {eventBus} from '@/utils/eventBus'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  sessionId: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'bind-success'])

const dialogVisible = ref(false)
const bindForm = reactive({
  phone: '',
  captcha: ''
})
const captchaCooldown = ref(0)
const loading = ref(false)
const phoneError = ref('')
const captchaError = ref('')
const captchaSuccess = ref('')

// 监听modelValue变化
watch(() => props.modelValue, (newVal) => {
  dialogVisible.value = newVal
})

// 监听dialogVisible变化
watch(dialogVisible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 永真
// watch(() => props.modelValue, (newVal) => {
//   dialogVisible.value = newVal
// }, { immediate: true })

// 清除手机号错误
const clearPhoneError = () => {
  phoneError.value = ''
}

// 清除验证码错误
const clearCaptchaError = () => {
  captchaError.value = ''
  captchaSuccess.value = ''
}

// 发送验证码
const sendCaptcha = async () => {
  clearPhoneError()
  clearCaptchaError()

  if (!bindForm.phone) {
    phoneError.value = '请输入手机号'
    return
  }

  if (!/^1[3-9]\d{9}$/.test(bindForm.phone)) {
    phoneError.value = '手机号格式不正确'
    return
  }

  try {
    const response = await request.get('/auth/verify-code/phone', {
      params: {
        phone: bindForm.phone,
        scene: 'Bind'
      }
    })

    if (response.code === '200') {
      captchaSuccess.value = '验证码已发送'
      // 开始倒计时
      captchaCooldown.value = 60
      const timer = setInterval(() => {
        captchaCooldown.value--
        if (captchaCooldown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    } else {
      ElMessage.error(response.message || '验证码发送失败')
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || '验证码发送失败'
    ElMessage.error(errorMsg)
  }
}

// 处理绑定
const handleBind = async () => {
  clearPhoneError()
  clearCaptchaError()
  loading.value = true

  if (!bindForm.phone) {
    phoneError.value = '请输入手机号'
    loading.value = false
    return
  }

  if (!bindForm.captcha) {
    captchaError.value = '请输入验证码'
    loading.value = false
    return
  }

  try {
    const response = await request.post('/auth/bind-phone/wechat', {
      phone: bindForm.phone,
      verifyCode: bindForm.captcha,
      sessionId: props.sessionId
    })

    if (response.code === '200') {
      // 保存token到localStorage
      localStorage.setItem('auth_token', response.data.token)

      eventBus.emitLogin()

      // ElMessage.success('手机绑定成功')
      emit('bind-success')
      dialogVisible.value = false
    } else {
      captchaError.value = response.message || '绑定失败，请重试'
    }
  } catch (error) {
    if (error.response?.data?.message) {
      if (error.response.data.message.includes('手机')) {
        phoneError.value = error.response.data.message
      } else if (error.response.data.message.includes('验证码')) {
        captchaError.value = error.response.data.message
      } else {
        ElMessage.error(error.response.data.message)
      }
    } else {
      ElMessage.error('绑定失败，请重试')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
/* ========== 弹窗容器 (2026 现代圆角 + 渐变光晕头部) ========== */

.dialog-custom-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 5px;
}

.dialog-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
}

.bind-phone-dialog :deep(.el-dialog) {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.16), 0 8px 24px rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.bind-phone-dialog :deep(.el-dialog__header) {
  padding: 22px 24px 16px;
  margin: 0;
  border-bottom: 1px solid rgba(241, 245, 249, 0.9);
  background: radial-gradient(ellipse 220px 70px at 10% 0%, rgba(59, 130, 246, 0.08), transparent 70%),
  radial-gradient(ellipse 180px 60px at 90% 10%, rgba(249, 115, 22, 0.06), transparent 70%),
  #ffffff;
}

.bind-phone-dialog :deep(.el-dialog__title) {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
}

.bind-phone-dialog :deep(.el-dialog__body) {
  padding: 24px;
}

.bind-phone-dialog :deep(.el-dialog__footer) {
  padding: 0 24px 24px;
}

/* ========== 输入框 ========== */
.bind-phone-dialog :deep(.el-input__wrapper) {
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(226, 232, 240, 0.9) inset;
  height: 46px;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  background: rgba(248, 250, 252, 0.4);
}

.bind-phone-dialog :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #cbd5e1 inset;
  background: #fff;
}

.bind-phone-dialog :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #3b82f6 inset, 0 0 0 4px rgba(59, 130, 246, 0.12) !important;
  background: #fff;
}

.bind-phone-dialog :deep(.el-input__prefix .el-icon) {
  color: #3b82f6;
  font-size: 18px;
}

.bind-phone-dialog :deep(.el-input__inner) {
  font-size: 14px;
  color: #1e293b;
}

.bind-phone-dialog :deep(.el-input__inner::placeholder) {
  color: #94a3b8;
}

/* ========== 错误/成功提示 ========== */
.error-message {
  display: flex;
  align-items: center;
  color: #ef4444;
  font-size: 0.78rem;
  margin-top: 6px;
  height: 20px;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

.success-message {
  display: flex;
  align-items: center;
  color: #16a34a;
  font-size: 0.78rem;
  margin-top: 6px;
  height: 20px;

  .el-icon {
    margin-right: 4px;
    font-size: 14px;
  }
}

/* ========== 验证码按钮 ========== */
.captcha-btn {
  padding: 0;
  height: auto;
  color: #3b82f6;
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
  transition: color 0.25s ease;
}

.captcha-btn:hover {
  color: #f97316;
}

.captcha-btn:disabled {
  color: #94a3b8;
  cursor: not-allowed;
}

/* ========== 微信绑定提示卡 ========== */
.wechat-bind-tip {
  margin-top: 14px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(249, 115, 22, 0.04));
  border: 1px solid rgba(59, 130, 246, 0.12);
  border-radius: 12px;
  font-size: 0.8rem;
  color: #475569;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.wechat-bind-tip::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #f97316);
  flex-shrink: 0;
}

.wechat-bind-tip p {
  margin: 0;
  line-height: 1.5;
}

.wechat-bind-tip strong {
  color: #f56c6c;
}

/* ========== 绑定按钮 ========== */
.bind-phone-dialog :deep(.el-button--primary) {
  height: 46px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.bind-phone-dialog :deep(.el-button--primary:hover) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
}

.bind-phone-dialog :deep(.el-button--primary:active) {
  transform: translateY(0);
}
</style>
